// frontend/src/pages/admin/CoupleSettings.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCouple } from '../../contexts/CoupleContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import DatePicker from 'react-datepicker';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import SubscriptionSettings from '../../components/subscription/SubscriptionSettings';

const CoupleSettings: React.FC = () => {
  const { currentCouple, updateCouple } = useCouple();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription'>('profile');
  
  const [names, setNames] = useState<string>('');
  const [weddingDate, setWeddingDate] = useState<Date>(new Date());
  const [weddingLocation, setWeddingLocation] = useState<string>('');
  const [customUrl, setCustomUrl] = useState<string>('');
  const [story, setStory] = useState<string>('');
  const [fundingGoal, setFundingGoal] = useState<string>('0');
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  useEffect(() => {
    if (currentCouple) {
      setNames(currentCouple.names);
      setWeddingDate(new Date(currentCouple.weddingDate));
      setWeddingLocation(currentCouple.weddingLocation || '');
      setCustomUrl(currentCouple.customUrl);
      setStory(currentCouple.story || '');
      setFundingGoal(currentCouple.fundingGoal?.toString() || '0');
    }
  }, [currentCouple]);
  
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!currentCouple) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await updateCouple(currentCouple._id, {
        names,
        weddingDate: weddingDate.toISOString(),
        weddingLocation,
        customUrl,
        story,
        fundingGoal: Number(fundingGoal),
      });
      
      setSuccess('Wedding profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <p className="text-center text-gray-500">Please create a couple profile first.</p>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings</h2>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('profile')}
              className={`${
                activeTab === 'profile'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Couple Profile
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`${
                activeTab === 'subscription'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Subscription
            </button>
          </nav>
        </div>
        
        {error && (
          <div className="mb-6">
            <Alert
              type="error"
              message={error}
              dismissible
              onDismiss={() => setError(null)}
            />
          </div>
        )}
        
        {success && (
          <div className="mb-6">
            <Alert
              type="success"
              message={success}
            />
          </div>
        )}
        
        {activeTab === 'profile' ? (
          <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
            <Input
              id="names"
              label="Couple Names"
              required
              value={names}
              onChange={(e) => setNames(e.target.value)}
              placeholder="e.g. Sarah & Michael"
            />
            
            <div>
              <label htmlFor="wedding-date" className="block text-sm font-medium text-gray-700">
                Wedding Date
              </label>
              <div className="mt-1">
                <DatePicker
                  id="wedding-date"
                  selected={weddingDate}
                  onChange={(date: Date) => setWeddingDate(date)}
                  dateFormat="MMMM d, yyyy"
                  className="shadow-sm focus:ring-pink-500 focus:border-pink-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <Input
              id="wedding-location"
              label="Wedding Location"
              value={weddingLocation}
              onChange={(e) => setWeddingLocation(e.target.value)}
              placeholder="e.g. Sunset Beach Resort, Malibu"
            />
            
            <Input
              id="custom-url"
              label="Custom URL"
              required
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              helperText="This is the URL of your wedding page"
            />
            
            <Textarea
              id="story"
              label="Your Story"
              rows={4}
              value={story}
              onChange={(e) => setStory(e.target.value)}
              placeholder="Tell your love story. How did you meet? How was the proposal?"
            />
            
            <div>
              <label htmlFor="funding-goal" className="block text-sm font-medium text-gray-700">
                Funding Goal
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="funding-goal"
                  min="0"
                  value={fundingGoal}
                  onChange={(e) => setFundingGoal(e.target.value)}
                  className="focus:ring-pink-500 focus:border-pink-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">USD</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
              >
                Save Changes
              </Button>
            </div>
          </form>
        ) : (
          <div className="bg-white shadow rounded-lg p-6">
            <SubscriptionSettings />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CoupleSettings;